import React, { useState, useEffect, useCallback } from 'react';
import './Workflows.css';
import WorkflowTemplateList from './WorkflowTemplateList';
import WorkflowTemplateEditor from './WorkflowTemplateEditor';
import WorkflowExecutionList from './WorkflowExecutionList';
import WorkflowExecutionView from './WorkflowExecutionView';
import WorkflowLaunchModal from './WorkflowLaunchModal';

/**
 * Conteneur du module Workflows.
 * Onglets : 📋 Modèles / 📊 Exécutions. Modes : list | edit | view.
 */
export default function Workflows({ user, editMode, setHeaderAction }) {
  const [tab, setTab] = useState('templates');     // templates | executions
  const [mode, setMode] = useState('list');         // list | edit | view
  const [templateId, setTemplateId] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isSuperAdmin = user?.role === 'super-admin';

  // Deep-link depuis le panneau latéral (« Voir en détail »)
  useEffect(() => {
    const handler = (e) => {
      setTab('executions');
      setExecutionId(e.detail?.executionId);
      setMode('view');
    };
    window.addEventListener('open-workflow-execution', handler);
    return () => window.removeEventListener('open-workflow-execution', handler);
  }, []);

  const openNewTemplate = useCallback(() => { setTemplateId(null); setMode('edit'); }, []);

  // Bouton d'action du header selon l'onglet / le mode
  useEffect(() => {
    if (!setHeaderAction) return;
    if (mode !== 'list') { setHeaderAction(null); return; }
    if (tab === 'templates') {
      setHeaderAction({ primary: { label: '+ Nouveau modèle', onClick: openNewTemplate } });
    } else {
      setHeaderAction({ primary: { label: '+ Lancer une exécution', onClick: () => setLaunchOpen(true) } });
    }
  }, [tab, mode, setHeaderAction, openNewTemplate]);

  const backToList = () => { setMode('list'); setTemplateId(null); setExecutionId(null); setRefreshKey(k => k + 1); };

  // ── Éditeur de modèle ──
  if (mode === 'edit') {
    return (
      <div className="page-container">
        <WorkflowTemplateEditor
          templateId={templateId}
          onBack={backToList}
          onSaved={(saved) => { setTemplateId(saved.id); }}
        />
      </div>
    );
  }

  // ── Vue d'exécution ──
  if (mode === 'view' && executionId) {
    return (
      <div className="page-container">
        <WorkflowExecutionView
          executionId={executionId}
          user={user}
          onBack={backToList}
        />
      </div>
    );
  }

  // ── Listes ──
  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'templates' ? 'active' : ''} onClick={() => setTab('templates')}>📋 Modèles</button>
        <button className={tab === 'executions' ? 'active' : ''} onClick={() => setTab('executions')}>📊 Exécutions</button>
      </div>

      {tab === 'templates' ? (
        <WorkflowTemplateList
          key={`tpl-${refreshKey}`}
          user={user}
          editMode={editMode}
          isSuperAdmin={isSuperAdmin}
          onEdit={(id) => { setTemplateId(id); setMode('edit'); }}
          onLaunch={(id) => { setLaunchOpen(true); setTemplateId(id); }}
        />
      ) : (
        <WorkflowExecutionList
          key={`exe-${refreshKey}`}
          user={user}
          editMode={editMode}
          isSuperAdmin={isSuperAdmin}
          onOpen={(id) => { setExecutionId(id); setMode('view'); }}
        />
      )}

      {launchOpen && (
        <WorkflowLaunchModal
          presetTemplateId={tab === 'templates' ? templateId : null}
          onClose={() => { setLaunchOpen(false); setTemplateId(null); }}
          onLaunched={(exec) => { setLaunchOpen(false); setTemplateId(null); setExecutionId(exec.id); setTab('executions'); setMode('view'); }}
        />
      )}
    </div>
  );
}
